// The island's components are also type-checked by the Worker, without the DOM lib, so the
// few browser calls they make go through these structural types. Each is a no-op on the server.

interface ElementLike {
  focus?(options?: { preventScroll?: boolean }): void;
  scrollIntoView?(options?: { block?: string; behavior?: string }): void;
  contains?(other: unknown): boolean;
}

interface DocumentLike {
  getElementById(id: string): unknown;
  addEventListener(type: string, listener: (event: { target: unknown }) => void): void;
  removeEventListener(type: string, listener: (event: { target: unknown }) => void): void;
}

function documentLike(): DocumentLike | undefined {
  return (globalThis as { document?: DocumentLike }).document;
}

/** An input, select or textarea event's current value. */
export function inputValue(event: { target: unknown }): string {
  const value = (event.target as { value?: unknown }).value;
  return typeof value === "string" ? value : "";
}

export function elementById(id: string): ElementLike | null {
  return (documentLike()?.getElementById(id) as ElementLike | null | undefined) ?? null;
}

export function focusElement(element: unknown, preventScroll = false): void {
  (element as ElementLike | null)?.focus?.({ preventScroll });
}

export function selectText(element: unknown): void {
  (element as { select?(): void } | null)?.select?.();
}

export function containsTarget(element: unknown, target: unknown): boolean {
  return (element as ElementLike | null)?.contains?.(target) ?? false;
}

export function prefersReducedMotion(): boolean {
  // Called on globalThis, not detached: an unbound matchMedia throws "Illegal invocation".
  return (
    (globalThis as { matchMedia?(query: string): { matches: boolean } }).matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches ?? false
  );
}

interface BoxLike {
  offsetTop: number;
  offsetHeight: number;
  scrollTop: number;
  clientHeight: number;
}

/**
 * Scrolls `container` just enough to show `child`, without scrolling the page. `child` must
 * be positioned relative to `container` (its offsetParent).
 */
export function scrollChildIntoView(container: unknown, child: unknown): void {
  const box = container as BoxLike | null;
  const item = child as BoxLike | null;
  if (!box || !item || typeof item.offsetTop !== "number") return;
  if (item.offsetTop < box.scrollTop) box.scrollTop = item.offsetTop;
  else if (item.offsetTop + item.offsetHeight > box.scrollTop + box.clientHeight) {
    box.scrollTop = item.offsetTop + item.offsetHeight - box.clientHeight;
  }
}

/** Calls back on a pointer press anywhere on the page. Returns the unsubscribe. */
export function onDocumentPointerDown(listener: (target: unknown) => void): () => void {
  const doc = documentLike();
  if (!doc) return () => {};
  const handler = (event: { target: unknown }) => listener(event.target);
  doc.addEventListener("pointerdown", handler);
  return () => doc.removeEventListener("pointerdown", handler);
}
